import React, { useState, useEffect } from 'react';
import api from '../../api/accounting';
import { toast } from 'react-toastify';
import { showConfirm } from '../../utils/confirmUtils';
import { Card, Input, Select, Label, Button, Badge, Icon } from '../../components/ui';

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    code: '',
    label: '',
    class: '4',
    type: 'asset',
    parentCode: '',
    level: 4,
    active: true
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const res = await api.get(`/chart-of-accounts?${params}`);

      const data = res.data;
      if (data.accounts && Array.isArray(data.accounts)) {
        setAccounts(data.accounts);
      } else if (Array.isArray(data)) {
        setAccounts(data);
      } else {
        console.warn('Format de réponse inattendu:', data);
        setAccounts([]);
      }
    } catch (e) {
      console.error('Erreur chargement:', e);
      toast.error(e.response?.data?.error || 'Erreur chargement');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/chart-of-accounts/${editingId}`, form);
        toast.success('Compte modifié');
      } else {
        await api.post('/chart-of-accounts', form);
        toast.success('Compte créé');
      }
      setForm({ code: '', label: '', class: '4', type: 'asset', parentCode: '', level: 4, active: true });
      setShowForm(false);
      setEditingId(null);
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    }
  };

  const editAccount = (account) => {
    setForm(account);
    setEditingId(account._id);
    setShowForm(true);
  };

  const deleteAccount = async (id) => {
    if (!await showConfirm('Supprimer ce compte ?')) return;
    try {
      await api.delete(`/chart-of-accounts/${id}`);
      toast.success('Compte supprimé');
      loadAccounts();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const classTypes = {
    '1': ['equity', 'provision', 'liability', 'capital'],
    '2': ['asset'],
    '3': ['stock'],
    '4': ['asset', 'liability', 'vat-collected', 'vat-deductible'],
    '5': ['bank', 'cash'],
    '6': ['expense'],
    '7': ['revenue']
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('Erreur téléchargement');
    }
  };

  const handleImportXlsx = async (file) => {
    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await api.post('/chart-of-accounts/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Import OK: ${res.data.created} créés, ${res.data.updated} modifiés`);
      loadAccounts();
    } catch (e) {
      const msg = e.response?.data?.error || 'Erreur import';
      const details = e.response?.data?.details?.slice?.(0, 3)?.join(' | ');
      toast.error(details ? `${msg}: ${details}` : msg);
    }
  };

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <h1 className="text-3xl font-bold text-gray-800">
          <Icon name="IconSitemap" className="mr-3 text-indigo-600" />
          Plan Comptable Tunisien (8 chiffres)
        </h1>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => downloadFile('/chart-of-accounts/export', 'plan_comptable.xlsx')}
          >
            <Icon name="IconFileExcel" className="mr-2" /> Export Excel
          </Button>

          <Button
            variant="secondary"
            onClick={() => downloadFile('/chart-of-accounts/import-template', 'modele_import_plan_comptable.xlsx')}
          >
            <Icon name="IconDownload" className="mr-2" /> Modèle d'import
          </Button>

          <label className="inline-flex cursor-pointer">
            <Button variant="primary" className="pointer-events-none">
              <Icon name="IconUpload" className="mr-2" /> Import Excel
            </Button>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImportXlsx(f);
                e.target.value = '';
              }}
            />
          </label>

          <Button
            variant="ghost"
            onClick={() => {
              setForm({ code: '', label: '', class: '4', type: 'asset', parentCode: '', level: 4, active: true });
              setEditingId(null);
              setShowForm(true);
            }}
          >
            <Icon name="IconPlus" className="mr-2" /> Nouveau
          </Button>
        </div>
      </div>

      {/* Formulaire création/modif */}
      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-bold mb-4">{editingId ? 'Modifier' : 'Nouveau'} compte</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Code (8 chiffres)</Label>
              <Input
                required
                type="text"
                maxLength="8"
                pattern="\d{8}"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="41110000"
              />
            </div>
            <div>
              <Label required>Libellé</Label>
              <Input
                required
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Clients TTC"
              />
            </div>
            <div>
              <Label required>Classe</Label>
              <Select
                required
                value={form.class}
                onChange={(e) => setForm({ ...form, class: e.target.value, type: classTypes[e.target.value][0] })}
              >
                <option value="1">1 - Capitaux/Passifs</option>
                <option value="2">2 - Immobilisations</option>
                <option value="3">3 - Stocks</option>
                <option value="4">4 - Tiers</option>
                <option value="5">5 - Trésorerie</option>
                <option value="6">6 - Charges</option>
                <option value="7">7 - Produits</option>
              </Select>
            </div>
            <div>
              <Label required>Type</Label>
              <Select
                required
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                {classTypes[form.class]?.map(t => (
                  <option key={t} value={t}>{t.replace('-', ' ').toUpperCase()}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label required>Niveau</Label>
              <Select
                required
                value={form.level}
                onChange={(e) => setForm({ ...form, level: Number(e.target.value) })}
              >
                <option value={1}>1 - Classe</option>
                <option value={2}>2 - Sous-classe</option>
                <option value={3}>3 - Compte</option>
                <option value={4}>4 - Sous-compte</option>
              </Select>
            </div>
            <div>
              <Label>Code parent</Label>
              <Input
                type="text"
                maxLength="7"
                value={form.parentCode}
                onChange={(e) => setForm({ ...form, parentCode: e.target.value })}
                placeholder="411100"
              />
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="rounded border-gray-300 text-indigo-600"
                />
                <span className="ml-2 text-sm">Actif</span>
              </label>
              <div className="flex space-x-2">
                <Button type="submit" variant="success">
                  {editingId ? 'Modifier' : 'Créer'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    setForm({ code: '', label: '', class: '4', type: 'asset', parentCode: '', level: 4, active: true });
                  }}
                >
                  Annuler
                </Button>
              </div>
            </div>
          </form>
        </Card>
      )}

      {/* Tableau des comptes */}
      <Card padding={false}>
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-wrap gap-2 mb-2">
            <button onClick={() => loadAccounts({ class: '4' })} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs hover:bg-blue-200 transition-colors">
              Tiers
            </button>
            <button onClick={() => loadAccounts({ class: '5' })} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs hover:bg-green-200 transition-colors">
              Trésorerie
            </button>
            <button onClick={() => loadAccounts({ class: '6' })} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs hover:bg-orange-200 transition-colors">
              Charges
            </button>
            <button onClick={() => loadAccounts({ class: '7' })} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs hover:bg-purple-200 transition-colors">
              Produits
            </button>
            <button onClick={() => loadAccounts({})} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs hover:bg-gray-200 transition-colors">
              Tous
            </button>
          </div>
          <Input
            type="text"
            placeholder="Rechercher code ou libellé..."
            onChange={(e) => loadAccounts({ search: e.target.value })}
            className="max-w-md"
          />
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Libellé</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actif</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {accounts.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  <Icon name="IconInbox" size={36} className="mb-2 block text-gray-300 mx-auto" />
                  Aucun compte trouvé
                </td>
              </tr>
            ) : (
              accounts.map((account) => (
                <tr key={account._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-semibold text-indigo-900">
                    {account.code}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{account.label}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      account.class === '1' ? 'bg-yellow-100 text-yellow-800' :
                      account.class === '2' ? 'bg-blue-100 text-blue-800' :
                      account.class === '3' ? 'bg-green-100 text-green-800' :
                      account.class === '4' ? 'bg-indigo-100 text-indigo-800' :
                      account.class === '5' ? 'bg-emerald-100 text-emerald-800' :
                      account.class === '6' ? 'bg-orange-100 text-orange-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      Classe {account.class}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant="gray">{account.type}</Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Niv. {account.level}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={account.active}
                      onChange={(e) => {
                        api.put(`/chart-of-accounts/${account._id}`, { active: e.target.checked })
                          .then(() => loadAccounts())
                          .catch(() => toast.error('Erreur'));
                      }}
                      className="rounded border-gray-300 text-indigo-600 shadow-sm"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => editAccount(account)}
                      title="Modifier"
                    >
                      <Icon name="IconEdit" />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => deleteAccount(account._id)}
                      title="Supprimer"
                    >
                      <Icon name="IconTrash" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
